import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { Button } from 'components/Button';
import { FormRow } from 'components/Form';
import GeneralError from 'components/GeneralError';
import Spinner from 'components/Spinner';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction, checkinAction } from 'actions/workflow';
import { SEND_TEXT_COOLDOWN_SECONDS } from 'utils/constants';
import { numberUnmask } from 'utils/masks';

const Otp = ({
  navigate,
  searchParams,
  dispatch,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const phoneLastFour = useSelector((state) => get(state, 'workflow.data.mobileLast4', '').slice(-4));
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendStatus, setResendStatus] = useState(null);
  const [generalError, setGeneralError] = useState(null);

  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [code3, setCode3] = useState('');
  const [code4, setCode4] = useState('');
  const [code5, setCode5] = useState('');
  const [code6, setCode6] = useState('');

  const code = useMemo(
    () => `${code1}${code2}${code3}${code4}${code5}${code6}`,
    [code1, code2, code3, code4, code5, code6],
  );

  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const verificationRef = useRef();

  const setCodeForIndex = (value, index) => {
    const setters = [setCode1, setCode2, setCode3, setCode4, setCode5, setCode6];
    if (setters[index]) setters[index](value);
  };

  const handlePaste = (e) => {
    const data = e.target.value;
    if (data) {
      const dataArray = numberUnmask(data).substring(0, 6).split('');
      if (dataArray.length > 0) {
        dataArray.forEach((char, index) => {
          setCodeForIndex(char, index);
        });
        inputRefs[dataArray.length - 1].current.focus();
      }
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    if (!(e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter') && !(/^\d*$/.test(value))) {
      return;
    }
    if (value.length > 2) {
      handlePaste(e);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      setCodeForIndex('', index - 1);
      inputRefs[index - 1].current.focus();
    } else if (e.key === 'Backspace' && e.target.value.length > 0) {
      setCodeForIndex('', index);
    }

    if (/^\d$/.test(e.key)) {
      setCodeForIndex(e.key, index);
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const handleFail = (err) => {
    setIsSubmitting(false);
    setIsFirstAttempt(false);

    const errorMessage = get(err, 'data.failure.errors.0.message')
      || get(err, 'data.errorMessage')
      || get(err, 'data.errorMessages.0');

    if (errorMessage) {
      setGeneralError({
        message: errorMessage,
        subject: get(err, 'data.failure.errors.0.subject'),
      });
    }
    [setCode1, setCode2, setCode3, setCode4, setCode5, setCode6].forEach((setter) => setter(''));

    if (verificationRef.current) {
      verificationRef.current.classList.add('animate-shake');
      setTimeout(() => {
        verificationRef.current?.classList.remove('animate-shake');
      }, 500);
    }
  };

  const getRecaptchaToken = useCallback(async (action) => {
    if (!executeRecaptcha) return null;
    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (err) {
      console.error('reCAPTCHA execution failed:', err);
      return null;
    }
  }, [executeRecaptcha]);

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    setIsSubmitting(true);
    setGeneralError(null);

    const recaptchaToken = await getRecaptchaToken('otp_verify');

    dispatch(nextAction({
      key: searchParams.get('key'),
      data: { code, token: recaptchaToken },
      success: (res) => {
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
        setIsSubmitting(false);
        setIsFirstAttempt(false);
      },
      fail: handleFail,
    }));
  };

  const handleResend = () => {
    if (cooldownSeconds > 0 || isResending) return;

    setIsResending(true);
    setGeneralError(null);
    setResendStatus(null);

    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: { resendText: true },
      success: () => {
        setIsResending(false);
        setResendStatus('success');
        setCooldownSeconds(SEND_TEXT_COOLDOWN_SECONDS);
      },
      fail: (err) => {
        setIsResending(false);
        setResendStatus('error');
        setCooldownSeconds(SEND_TEXT_COOLDOWN_SECONDS);
        handleFail(err);
      },
    }));
  };

  useEffect(() => {
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, []);

  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  useEffect(() => {
    if (code && code.length === 6 && isFirstAttempt) {
      handleSubmit();
    }
  }, [code]);

  useEffect(() => {
    if (resendStatus) {
      const timeout = setTimeout(() => setResendStatus(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [resendStatus]);

  const codeValues = [code1, code2, code3, code4, code5, code6];

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="text-center">
        <p className="text-gray-600">
          Please enter the verification code sent to (xxx) xxx-{phoneLastFour}
        </p>
      </div>

      <form className="w-full flex flex-col items-center gap-6" onSubmit={handleSubmit}>
        <div className="flex gap-2 sm:gap-3 justify-center w-full max-w-[320px] px-2" ref={verificationRef}>
          {codeValues.map((value, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={value}
              onChange={handleInputChange}
              onKeyDown={(e) => handleKeyDown(e, index)}
              ref={inputRefs[index]}
              className="flex-1 min-w-0 aspect-square max-w-[48px] text-center text-base sm:text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        <GeneralError className="!mb-0" error={generalError} />

        
        <FormRow className="w-80 max-w-full !gap-2 !flex-col">
          {!isFirstAttempt || isSubmitting ? (
            <Button
              className="w-full"
              label="Verify Code & Continue"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSubmitting || !code || code.length !== 6}
            />
          ) : null}
          <p className="text-zinc-500 text-xs">
            This site is protected by reCAPTCHA and the Google
            {' '}
            <a className="transition text-blue-500 hover:text-blue-700" href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a>
            {' '}
            and
            {' '}
            <a className="transition text-blue-500 hover:text-blue-700" href="https://policies.google.com/terms" target="_blank">Terms of Service</a>
            {' '}
            apply.
          </p>
        </FormRow>

        {isResending ? (
          <Spinner size={4} color="blue-500" />
        ) : (
          <div className="flex flex-col items-center gap-2">
            {resendStatus === 'success' && (
              <p className="text-sm text-green-600 font-medium" role="status">
                ✓ Verification code sent successfully
              </p>
            )}
            {resendStatus === 'error' && (
              <p className="text-sm text-red-600 font-medium" role="alert">
                ✗ Failed to send verification code
              </p>
            )}
            <button
              type="button"
              className={`text-sm transition-colors ${
                cooldownSeconds > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-500 hover:text-blue-700 underline'
              }`}
              onClick={handleResend}
              disabled={cooldownSeconds > 0}
              aria-disabled={cooldownSeconds > 0}
            >
              Didn&apos;t receive the text? Resend verification code.
            </button>
            {cooldownSeconds > 0 && (
              <p className="text-sm text-gray-500">
                You can resend a new code in {cooldownSeconds}s
              </p>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

Otp.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default Otp;
